import type { DateTime } from 'luxon';
import { useCallback, useRef, useState } from 'react';
import reactHtmlParser from 'react-html-parser';
import styles from './RecommendModal.module.scss';
import { useUser } from '~/api/hooks/useGetAccount';
import { postProductImpression } from '~/api/productImpression';
import { CommonBasicButton } from '~/components/common/button/CommonBasicButton';
import { CommonUnderCloseModal } from '~/components/common/modal/CommonUnderCloseModal';
import { BasicParagraph } from '~/components/common/text/BasicParagraph';
import { RecommendationShoes } from '~/features/recommend/components/RecommendationShoes/index';
import { RecommendationShoesMeta } from '~/features/recommend/components/RecommendationShoesMeta';
import { useCommonProductsImpression } from '~/features/recommend/hooks/useCommonProductsImpression';
import type { Product } from '~/features/recommend/interfaces/models';
import { useDate } from '~/hooks/useDate';
import { useRequestApi } from '~/hooks/useRequestApi';
import { useTranslation } from '~/hooks/useTranslation';
import { toEcSite } from '~/utils/toEcSite';

interface Props {
  products: Product[];
  page: 'home' | 'predict';
  selectedProductIndex: number;
  children: JSX.Element | null;
  date?: DateTime;
  title: string;
  imageType?: 'alt' | 'side';
}

export const RecommendModal = ({
  products,
  page,
  selectedProductIndex,
  children,
  date,
  title,
  imageType,
}: Props) => {
  const [productIndex, setProductIndex] = useState<number>(selectedProductIndex);
  const { t } = useTranslation();
  const { data: user } = useUser();
  const postProductImpressionRequest = useRequestApi(postProductImpression);
  const { formatRegionDateYMD } = useDate();
  const observer = useRef<HTMLDivElement>(null);
  const IMP_LIST_NAME = page === 'home' ? 'home_modal_recommend' : 'predict_modal_recommend';
  useCommonProductsImpression(IMP_LIST_NAME, observer, products);

  const toEC = useCallback(async () => {
    if (!user) return;
    await postProductImpressionRequest({
      child_id: user.selected_child_id,
      behavior: 'conversion',
    });
    toEcSite(
      page,
      IMP_LIST_NAME,
      productIndex + 1,
      t('to_ec_confirm').toString(),
      products[productIndex]?.link,
      products[productIndex],
    );
  }, [postProductImpressionRequest, user, page, IMP_LIST_NAME, productIndex, t, products]);

  return (
    <CommonUnderCloseModal size="672-1008">
      <div className={styles.wrapper} ref={observer}>
        {children}
        <div className={styles.container}>
          {date ? (
            <BasicParagraph className={styles.date} size={28} weight={900}>
              {formatRegionDateYMD(date)}
            </BasicParagraph>
          ) : (
            <></>
          )}
          <BasicParagraph className={styles.title} size={28} weight={900} lineHeight={1.4}>
            {reactHtmlParser(title)}
          </BasicParagraph>
          <RecommendationShoes
            products={products}
            selectedProductIndex={productIndex}
            setSelectedProductIndex={setProductIndex}
            imageType={imageType}
          />
          <RecommendationShoesMeta
            name={products[productIndex].name}
            shoeSize={products[productIndex].size}
            category={products[productIndex].item_category ?? ''}
            description={products[productIndex].description}
          />
          <CommonBasicButton
            size="400-112"
            colorType="positive"
            text="home_btn_ec"
            onClick={toEC}
          />
        </div>
      </div>
    </CommonUnderCloseModal>
  );
};
